import React, { FC, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { useHistory } from 'react-router-dom';
import { WalletIcon } from '../../commons/icons/Wallet';
import { useAppSelector } from '../../../hooks/redux';
import { CustomMUIButton } from '../../commons/buttons/CustomMUIButton';
import { CustomNumberFormat } from '../../commons/CustomNumberFormat';
import { ButtonClassNames, GlobalRouteNames, TopUpRouteNames } from '../../../models/Common';

interface WalletItem {
  label: string;
  value: JSX.Element | string;
  valueClass: string;
}

interface WalletCardProps {
  handleClose: () => void;
}

export const WalletCard: FC<WalletCardProps> = ({ handleClose }) => {
  const { userInfoState, isUserInfoDownloaded } = useAppSelector((state) => state.userInfoReducer);
  const history = useHistory();
  const { t } = useTranslation();
  const isBonusesAvailable = userInfoState.Permissions.Info.ShowBonuses;
  const canAddMoney = userInfoState.Permissions.Payments.AddMoney;

  const handleLink = useCallback(
    (link: string) => {
      handleClose();
      history.push(link);
    },
    [history],
  );

  const walletItems: WalletItem[] = [
    { label: t('wallet.numberOfContract'), value: `${userInfoState.id}`, valueClass: 'agreement' },
    {
      label: t('wallet.yourBalance'),
      value: Number.isInteger(userInfoState.Balance) ? (
        `${userInfoState.Balance} ${t('common.uah')}`
      ) : (
        <CustomNumberFormat value={userInfoState.Balance} suffix={t('common.uah')} />
      ),
      valueClass: 'value',
    },
    {
      label: t('wallet.yourBonuses'),
      value: Number.isInteger(userInfoState.Bonus) ? (
        `${userInfoState.Bonus}`
      ) : (
        <CustomNumberFormat value={userInfoState.Bonus} />
      ),
      valueClass: 'value',
    },
  ];

  return (
    <div className="wallet-card">
      <div className="wallet-card__content">
        <div className="wallet-card__content__user">
          <p>{userInfoState.Name}</p>
        </div>
        <div className="wallet-card__content__balance">
          <div className="wallet-card__content__balance__values">
            {isBonusesAvailable ? (
              <>
                {walletItems.map((el) => {
                  return (
                    <div className="item" key={el.label}>
                      <p className="label">{el.label}</p>
                      <p className={`${el.valueClass} ${!isUserInfoDownloaded ? 'skeleton' : 'skeleton--disabled'}`}>
                        {el.value}
                      </p>
                    </div>
                  );
                })}
              </>
            ) : (
              <>
                {walletItems.map(
                  (el) =>
                    el.label !== t('wallet.yourBonuses') && (
                      <div className="item" key={el.label}>
                        <p className="label">{el.label}</p>
                        <p className={`${el.valueClass} ${!isUserInfoDownloaded ? 'skeleton' : 'skeleton--disabled'}`}>
                          {el.value}
                        </p>
                      </div>
                    ),
                )}
              </>
            )}
          </div>
          <div className="wallet-card__content__balance__icon">
            <WalletIcon />
          </div>
        </div>
      </div>
      <div className="wallet-card__buttons">
        <CustomMUIButton
          label={t('buttons.topUpBalance')}
          handleClick={() => {
            handleLink(`${GlobalRouteNames.TopUpBalancePage}${TopUpRouteNames.BankCard}`);
          }}
          className={ButtonClassNames.SecondaryOutlinedSmall}
          isDisabled={!isUserInfoDownloaded || !canAddMoney}
        />
      </div>
    </div>
  );
};
